import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { scheduleUndoableDelete } from "@/lib/undoDelete";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash, Key, Users as UsersIcon, WhatsappLogo } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

export default function Users() {
  const { user: currentUser } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "petugas", wa_number: "" });
  const [submitting, setSubmitting] = useState(false);

  // Reset password dialog state
  const [resetOpen, setResetOpen] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPassword, setResetPassword] = useState("");
  const [resetting, setResetting] = useState(false);

  // Edit WA number state
  const [waOpen, setWaOpen] = useState(false);
  const [waTarget, setWaTarget] = useState(null);
  const [waValue, setWaValue] = useState("");
  const [waSaving, setWaSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/auth/users");
      setItems(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setForm({ email: "", password: "", name: "", role: "petugas", wa_number: "" });
    setOpen(true);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password || !form.name) {
      toast.error("Lengkapi semua field");
      return;
    }
    if (form.password.length < 6) {
      toast.error("Password minimal 6 karakter");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/register", form);
      toast.success(`User ${form.email} berhasil ditambahkan`);
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const onDelete = (u) => {
    if (currentUser?.id === u.id) {
      toast.error("Tidak bisa menghapus akun sendiri");
      return;
    }
    scheduleUndoableDelete({
      items, setItems, target: u,
      label: `User ${u.email}`,
      apiCall: () => api.delete(`/auth/users/${u.id}`, { params: { hard: true } }),
    });
  };

  const openReset = (u) => {
    setResetTarget(u);
    setResetPassword("");
    setResetOpen(true);
  };

  const onResetSubmit = async (e) => {
    e.preventDefault();
    if (resetPassword.length < 6) {
      toast.error("Password minimal 6 karakter");
      return;
    }
    setResetting(true);
    try {
      await api.post(`/auth/users/${resetTarget.id}/reset-password`, { password: resetPassword });
      toast.success(`Password ${resetTarget.email} berhasil direset`);
      setResetOpen(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setResetting(false);
    }
  };

  const openWa = (u) => {
    setWaTarget(u);
    setWaValue(u.wa_number || "");
    setWaOpen(true);
  };

  const onWaSubmit = async (e) => {
    e.preventDefault();
    setWaSaving(true);
    try {
      await api.patch(`/auth/users/${waTarget.id}`, { wa_number: waValue.trim() });
      toast.success(`Nomor WA ${waTarget.email} tersimpan`);
      setWaOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setWaSaving(false);
    }
  };

  const filtered = items.filter(
    (u) =>
      !q ||
      u.email.toLowerCase().includes(q.toLowerCase()) ||
      (u.name || "").toLowerCase().includes(q.toLowerCase()),
  );

  return (
    <div className="p-6 md:p-8 max-w-5xl">
      <div className="flex flex-col md:flex-row md:items-end justify-between border-b border-border pb-6 mb-6 gap-4">
        <div>
          <div className="overline">Master Data</div>
          <h1 className="text-3xl md:text-4xl font-black tracking-tight mt-2 flex items-center gap-3">
            <span className="w-11 h-11 rounded-full bg-orange-100 text-primary flex items-center justify-center shrink-0">
              <UsersIcon size={22} weight="bold" />
            </span>
            User
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Kelola akun admin dan petugas.
          </p>
        </div>
        <Button
          onClick={openNew}
          className="rounded-xl font-bold uppercase tracking-wider"
          data-testid="add-user-button"
        >
          <Plus size={16} className="mr-2" /> Tambah User
        </Button>
      </div>

      <Input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Cari email atau nama..."
        className="rounded-xl h-11 mb-4"
        data-testid="search-user-input"
      />

      <div className="border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-secondary/50 text-left">
            <tr>
              <th className="p-3 overline">Email</th>
              <th className="p-3 overline">Nama</th>
              <th className="p-3 overline">Role</th>
              <th className="p-3 overline">No WA</th>
              <th className="p-3 overline">Status</th>
              <th className="p-3 overline">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((u) => {
              const isSelf = currentUser?.id === u.id;
              const isActive = u.aktif !== false;
              return (
                <tr key={u.id} className="border-b border-border hover:bg-secondary/50">
                  <td className="p-3 font-mono">{u.email}</td>
                  <td className="p-3">{u.name || "-"}</td>
                  <td className="p-3">
                    <span
                      className={`status-pill ${
                        u.role === "admin" ? "status-diverifikasi"
                        : u.role === "admin_kantor" ? "status-termuat"
                        : "status-belum"
                      }`}
                    >
                      {u.role === "admin_kantor" ? "ADMIN KANTOR"
                        : u.role === "petugas" ? "PETUGAS LOADING"
                        : u.role.toUpperCase()}
                    </span>
                  </td>
                  <td className="p-3 font-mono text-xs">{u.wa_number || <span className="text-muted-foreground">-</span>}</td>
                  <td className="p-3">
                    <span
                      className={`status-pill ${isActive ? "status-termuat" : "status-belum"}`}
                    >
                      {isActive ? "AKTIF" : "NONAKTIF"}
                    </span>
                  </td>
                  <td className="p-3">
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-xl"
                        onClick={() => openWa(u)}
                        disabled={!isActive}
                        title="Set nomor WhatsApp"
                        data-testid={`wa-user-${u.email}`}
                      >
                        <WhatsappLogo size={14} />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-xl"
                        onClick={() => openReset(u)}
                        disabled={!isActive}
                        title="Reset password"
                        data-testid={`reset-user-${u.email}`}
                      >
                        <Key size={14} />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-xl text-destructive bg-destructive/5 border-destructive/40"
                        onClick={() => onDelete(u)}
                        disabled={isSelf}
                        title="Hapus permanen"
                        data-testid={`delete-user-${u.email}`}
                      >
                        <Trash size={14} weight="fill" />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={6} className="p-8 text-center text-muted-foreground">
                  Belum ada user.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-xl max-w-md">
          <DialogHeader>
            <DialogTitle>Tambah User</DialogTitle>
          </DialogHeader>
          <form onSubmit={onSubmit} className="space-y-4" data-testid="user-form">
            <div>
              <Label className="overline block mb-2">Email *</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                required
                placeholder="petugas@spb.local"
                className="rounded-xl h-11 font-mono"
                data-testid="input-user-email"
              />
            </div>
            <div>
              <Label className="overline block mb-2">Nama Lengkap *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                required
                placeholder="Nama Petugas"
                className="rounded-xl h-11"
                data-testid="input-user-name"
              />
            </div>
            <div>
              <Label className="overline block mb-2">Password *</Label>
              <Input
                type="password"
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                required
                minLength={6}
                placeholder="Min. 6 karakter"
                className="rounded-xl h-11"
                data-testid="input-user-password"
              />
            </div>
            <div>
              <Label className="overline block mb-2">Role *</Label>
              <Select
                value={form.role}
                onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}
              >
                <SelectTrigger className="rounded-xl h-11" data-testid="select-user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="admin_kantor">Admin Kantor</SelectItem>
                  <SelectItem value="petugas">Petugas Loading</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline block mb-2">No WhatsApp (opsional)</Label>
              <Input
                value={form.wa_number}
                onChange={(e) => setForm((f) => ({ ...f, wa_number: e.target.value }))}
                placeholder="08xxxxxxxxxx"
                className="rounded-xl h-11 font-mono"
                data-testid="input-user-wa"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Untuk petugas: nomor ini menerima notifikasi WA saat ada SPB baru.
              </p>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                className="rounded-xl"
                onClick={() => setOpen(false)}
              >
                Batal
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="rounded-xl font-bold uppercase"
                data-testid="save-user-button"
              >
                {submitting ? "Menyimpan..." : "Simpan"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent className="rounded-xl max-w-md">
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
          </DialogHeader>
          <form onSubmit={onResetSubmit} className="space-y-4" data-testid="reset-password-form">
            <div className="text-sm text-muted-foreground">
              Reset password untuk user{" "}
              <span className="font-mono font-bold text-foreground">
                {resetTarget?.email}
              </span>
            </div>
            <div>
              <Label className="overline block mb-2">Password Baru *</Label>
              <Input
                type="password"
                value={resetPassword}
                onChange={(e) => setResetPassword(e.target.value)}
                required
                minLength={6}
                placeholder="Min. 6 karakter"
                className="rounded-xl h-11"
                data-testid="input-reset-password"
                autoFocus
              />
              <p className="text-xs text-muted-foreground mt-1">
                Simpan password ini di tempat aman dan berikan ke user yang bersangkutan.
              </p>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                className="rounded-xl"
                onClick={() => setResetOpen(false)}
              >
                Batal
              </Button>
              <Button
                type="submit"
                disabled={resetting}
                className="rounded-xl font-bold uppercase"
                data-testid="save-reset-password-button"
              >
                {resetting ? "Menyimpan..." : "Reset Password"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog open={waOpen} onOpenChange={setWaOpen}>
        <DialogContent className="rounded-xl max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <WhatsappLogo size={18} /> Set Nomor WhatsApp
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={onWaSubmit} className="space-y-4" data-testid="wa-form">
            <div className="text-sm text-muted-foreground">
              User: <span className="font-mono font-bold text-foreground">{waTarget?.email}</span>
            </div>
            <div>
              <Label className="overline block mb-2">No WhatsApp</Label>
              <Input
                value={waValue}
                onChange={(e) => setWaValue(e.target.value)}
                placeholder="08xxxxxxxxxx"
                className="rounded-xl h-11 font-mono"
                data-testid="input-wa-number"
                autoFocus
              />
              <p className="text-xs text-muted-foreground mt-1">
                Kosongkan lalu simpan untuk menghapus. Notifikasi SPB baru akan dikirim ke nomor ini (untuk role petugas).
              </p>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                className="rounded-xl"
                onClick={() => setWaOpen(false)}
              >
                Batal
              </Button>
              <Button
                type="submit"
                disabled={waSaving}
                className="rounded-xl font-bold uppercase"
                data-testid="save-wa-button"
              >
                {waSaving ? "Menyimpan..." : "Simpan"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
